import type { CSSProp } from 'styled-components';
import styled, { css } from 'styled-components';

import { Box } from '@/components/Box';
import type { Colors, Space } from '@/theme/types';

import type { IconButtonProps } from './IconButton';

export const defaultProps: Required<
  Pick<IconButtonProps, 'as' | 'variant' | 'colorVariant' | 'size'>
> = {
  as: 'button',
  variant: 'filled',
  colorVariant: 'default',
  size: 'm',
};

export const switchSize = (size = defaultProps.size, space: Space) => {
  switch (size) {
    case 's':
      return space.s250;
    case 'm':
      return space.s300;
    case 'l':
      return space.s350;
    default:
      return 'auto';
  }
};

export const switchStyles = (
  variant = defaultProps.variant,
  colorVariant = defaultProps.colorVariant,
  colors: Colors,
  pxValue: Space[keyof Space],
  borderWidth: string,
): CSSProp => {
  const defaultDisabledStyles = css`
    &:disabled {
      pointer-events: none;

      ${variant === 'filled'
        ? css`
            background-color: ${colors.defaultAdd5};
            color: ${colors.defaultAdd2};
          `
        : css`
            border-color: ${colors.defaultAdd5};
            color: ${colors.defaultAdd3};
          `}
    }
  `;

  const outlinedIconButton = css`
    border: ${borderWidth} solid currentColor;
    background-color: ${colors.white};
    padding: 0 calc(${pxValue} - ${borderWidth});
  `;

  const ghostedIconButton = css`
    border: none;
    background-color: transparent;
  `;

  let colorStyles: CSSProp = '';

  switch (colorVariant) {
    case 'default':
      if (variant === 'filled') {
        colorStyles = css`
          background-color: ${colors.default};
          color: ${colors.white};

          &:focus,
          &:hover {
            background-color: ${colors.defaultAdd2};
          }

          &:active {
            background-color: ${colors.defaultAdd3};
          }
        `;
      }

      if (variant === 'outlined') {
        colorStyles = css`
          color: ${colors.default};

          &:focus,
          &:hover {
            color: ${colors.defaultAdd2};
          }

          &:active {
            background-color: ${colors.defaultAdd6};
            color: ${colors.default};
          }
        `;
      }

      if (variant === 'ghosted') {
        colorStyles = css`
          color: ${colors.default};

          &:focus,
          &:hover {
            background-color: ${colors.defaultAdd6};
          }

          &:active {
            background-color: ${colors.defaultAdd5};
          }

          &:disabled {
            color: ${colors.defaultAdd4};
          }
        `;
      }
      break;
    case 'defaultInverted':
      if (variant === 'filled') {
        colorStyles = css`
          background-color: ${colors.white};
          color: ${colors.default};

          &:focus,
          &:hover {
            color: ${colors.defaultAdd2};
          }

          &:active {
            background-color: ${colors.defaultAdd5};
            color: ${colors.default};
          }

          &:disabled {
            background-color: ${colors.defaultAdd2};
            color: ${colors.defaultAdd4};
          }
        `;
      }

      if (variant === 'outlined') {
        colorStyles = css`
          background-color: transparent;
          border-color: ${colors.white};
          color: ${colors.white};

          &:focus,
          &:hover {
            background-color: ${colors.whiteAlpha12};
          }

          &:active {
            background-color: ${colors.whiteAlpha16};
          }

          &:disabled {
            background-color: transparent;
            border-color: ${colors.whiteAlpha32};
            color: ${colors.whiteAlpha40};
          }
        `;
      }

      if (variant === 'ghosted') {
        colorStyles = css`
          color: ${colors.white};

          &:focus,
          &:hover {
            svg {
              color: ${colors.primary};
            }
          }

          &:active {
            background-color: ${colors.whiteAlpha24};
          }

          &:disabled {
            color: ${colors.defaultAdd3};
          }
        `;
      }

      break;
    case 'primary':
      if (variant === 'filled') {
        colorStyles = css`
          background-color: ${colors.primary};
          color: ${colors.white};

          &:focus,
          &:hover {
            background-color: ${colors.primaryAdd1};
          }

          &:active {
            background-color: ${colors.primarySub2};
          }
        `;
      }

      if (variant === 'outlined') {
        colorStyles = css`
          color: ${colors.primary};

          &:focus,
          &:hover {
            color: ${colors.primaryAdd1};
          }

          &:active {
            background-color: ${colors.defaultAdd6};
            color: ${colors.primarySub2};
          }
        `;
      }

      if (variant === 'ghosted') {
        colorStyles = css`
          color: ${colors.primary};

          &:focus,
          &:hover {
            background-color: ${colors.primaryAlpha4};
          }

          &:active {
            background-color: ${colors.primaryAlpha8};
          }

          &:disabled {
            color: ${colors.defaultAdd4};
          }
        `;
      }

      break;
    case 'destructive':
      if (variant === 'filled') {
        colorStyles = css`
          background-color: ${colors.destructive};
          color: ${colors.white};

          &:focus,
          &:hover {
            background-color: ${colors.destructiveAdd2};
          }

          &:active {
            background-color: ${colors.destructiveSub2};
          }
        `;
      }

      if (variant === 'outlined') {
        colorStyles = css`
          color: ${colors.destructive};

          &:focus,
          &:hover {
            color: ${colors.destructiveAdd2};
          }

          &:active {
            background-color: ${colors.defaultAdd6};
            color: ${colors.destructiveSub2};
          }
        `;
      }

      if (variant === 'ghosted') {
        colorStyles = css`
          color: ${colors.destructive};

          &:focus,
          &:hover {
            background-color: ${colors.destructiveAlpha4};
          }

          &:active {
            background-color: ${colors.destructiveAlpha8};
          }

          &:disabled {
            color: ${colors.defaultAdd4};
          }
        `;
      }
      break;

    default:
      break;
  }

  return css`
    ${defaultDisabledStyles}
    ${variant === 'outlined' && outlinedIconButton};
    ${variant === 'ghosted' && ghostedIconButton}
    ${colorStyles};
  `;
};

export const IconButtonContainer = styled(Box).attrs({
  textAlign: 'center',
  borderRadius: 'none',
})<IconButtonProps>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  white-space: nowrap;
  transition: background-color 0.15s ease-in-out, color 0.15s ease-in-out;

  &::before,
  &::after {
    white-space: pre;
  }

  ${({
    colorVariant,
    px = 's000',
    size,
    theme: {
      colors,
      components: { button },
      space,
    },
    variant,
  }) => {
    const sizeValue = switchSize(size, space);

    return css`
      height: ${sizeValue};
      min-width: ${sizeValue};
      width: ${sizeValue};

      ${switchStyles(
        variant,
        colorVariant,
        colors,
        space[px as keyof Space],
        `${button.borderWidth}px`,
      )};

      &:focus {
        outline: solid;
      }
    `;
  }};
`;
